import type { Scene } from 'three';
import { buildLevelFromPlacements, randomSpec } from '../editor/placement';
import { loadMaterials } from './materials';
import { mergeStaticMeshes } from './meshMerge';
import type { BuiltLevel } from './TestLevel';

/**
 * "The Expanse" — a huge generated free-surf course straight off the editor's
 * Generate backbone: a fixed-seed `randomSpec` at 500 features that fans into many
 * surf lanes. Plain (non-themed) skin + the default sky, untimed. No bespoke decor —
 * just the generated geometry. Per-ramp meshes are merged by material for perf.
 */

/** Fixed seed → a stable shippable course every load. */
const SEED = 0x0e15e500;

/** Deterministic PRNG (mulberry32) so the generated course is stable. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export async function buildExpanse(scene: Scene): Promise<BuiltLevel> {
  const materials = await loadMaterials();
  const spec = randomSpec(500, mulberry32(SEED));
  spec.id = 'surf_expanse';
  spec.name = 'The Expanse';
  const level = buildLevelFromPlacements(spec, scene, materials);
  // Perf: collapse the ~500 per-ramp meshes into one per material.
  const colliders = mergeStaticMeshes(scene, level.colliders);
  return { ...level, colliders, mapId: 'surf_expanse' };
}
